import { useRef } from "react";
import { twMerge } from "tailwind-merge";
import { X } from "lucide-react";
import { motion, AnimatePresence, type Variants } from "motion/react";
import { SheetBase, type SheetBaseProps } from "@core/ui";
import { Button } from "@themes/pollinator/ui/Button";

export type Props = Omit<SheetBaseProps, "renderBackdrop" | "renderPanel" | "renderCloseButton"> & {
  /** Additional CSS classes for the panel */
  className?: string;
  /** Additional CSS classes for the inner content container */
  innerClassName?: string;
};

/**
 * Styled sheet/drawer component wrapping SheetBase.
 * Slides in from the right with animations.
 *
 * For building a custom sheet with different styling, use SheetBase from @core/ui
 * which provides the core behavior without any styling.
 */
export function Sheet({
  children,
  className,
  innerClassName,
  isOpen,
  onClose,
  position = "right",
  "data-testid": testId = "sheet",
  ...props
}: Props) {
  const ref = useRef<HTMLDivElement>(null);

  const fadeDuration = 0.2;
  const slideOutDuration = 0.1;

  const backdropVariants: Variants = {
    enter: {
      opacity: 1,
      transition: { duration: fadeDuration, delay: 0 },
    },
    exit: {
      opacity: 0,
      transition: { duration: fadeDuration, delay: slideOutDuration },
    },
  };

  const panelVariants: Variants = {
    enter: {
      x: 0,
      transition: {
        duration: slideOutDuration,
        delay: fadeDuration,
        ease: "linear",
      },
    },
    exit: {
      x: "100%",
      transition: { duration: slideOutDuration, delay: 0, ease: "linear" },
    },
  };

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <SheetBase
          isOpen={isOpen}
          onClose={onClose}
          position={position}
          data-testid={testId}
          renderBackdrop={({ onClose: handleClose, children: panelContent }) => (
            <motion.div
              variants={backdropVariants}
              initial="exit"
              animate="enter"
              exit="exit"
              className="fixed inset-0 z-50 bg-brand-900/90 backdrop-blur-[4px]"
              onClick={handleClose}
              data-testid={`${testId}-backdrop`}
            >
              {panelContent}
            </motion.div>
          )}
          renderPanel={({ children: content }) => (
            <motion.div
              ref={ref}
              variants={panelVariants}
              initial="exit"
              animate="enter"
              exit="exit"
              role="dialog"
              aria-modal="true"
              data-testid={testId}
              className={twMerge(
                "absolute top-0 right-0 flex flex-col gap-4 bg-brand-50 h-full w-full max-w-xs justify-between",
                className
              )}
              onClick={(e) => e.stopPropagation()}
            >
              {content}
            </motion.div>
          )}
          renderCloseButton={({ onClose: handleClose }) => (
            <div className="absolute top-0 right-0 p-4 z-10">
              <Button
                variant="ghost"
                iconComponent={X}
                className="size-max"
                onClick={handleClose}
                data-testid={`${testId}-close-button`}
              />
            </div>
          )}
          {...props}
        >
          <div
            className={twMerge(
              "flex flex-col justify-between h-full",
              innerClassName
            )}
          >
            {children}
          </div>
        </SheetBase>
      )}
    </AnimatePresence>
  );
}

export default Sheet;

